import { useState } from "react"
import { useAuth } from "@clerk/clerk-react"
import { motion } from "framer-motion"
import { Check, MessageSquare, Sparkles, Loader2 } from "lucide-react"
import { useStudioStore } from "@/store/studio"
import { createProject, expandProject, developProject, approveProject } from "@/lib/api"
import type { Direction } from "@/types"
import { cn } from "@/lib/utils"
import { useLocation } from "wouter"

export default function DevelopView() {
  const [, navigate] = useLocation()
  const { getToken, isSignedIn } = useAuth()
  const store = useStudioStore()
  const [concept, setConcept] = useState(store.concept)
  const [format, setFormat] = useState("Short film")
  const [targetLength, setTargetLength] = useState("3–5 minutes")
  const [audience, setAudience] = useState("")
  const [freedom, setFreedom] = useState("Surprise me")
  const [loading, setLoading] = useState(false)
  const [showDirections, setShowDirections] = useState(store.directions.length > 0 && !store.story)
  const [showStory, setShowStory] = useState(!!store.story)
  const [activeDoc, setActiveDoc] = useState<"brief" | "script" | "episodes">("brief")
  const [storyTitle, setStoryTitle] = useState(store.story?.title || "")
  const [storyLogline, setStoryLogline] = useState(store.story?.logline || "")
  const [storyTheme, setStoryTheme] = useState(store.story?.theme || "")
  const [storyStyle, setStoryStyle] = useState(store.story?.visualLanguage || "")
  const [storyOutline, setStoryOutline] = useState(store.story?.outline || "")
  const [producerNote, setProducerNote] = useState("")

  const handleExplore = async () => {
    if (!concept.trim()) {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Give Atlas at least one spark to work with." } }))
      return
    }
    if (!isSignedIn) {
      setConcept(concept)
      store.setProject("", store.title, concept)
      const returnTo = `${window.location.pathname}${window.location.search}`
      window.location.assign(`/sign-in?redirect_url=${encodeURIComponent(returnTo)}`)
      return
    }
    const token = await getToken()
    if (!token) return
    ;(window as unknown as Record<string, unknown>).__atlas_token__ = token
    setLoading(true)
    try {
      const created = await createProject(concept, store.title, token)
      store.setProject(created.project.id, created.project.title, concept)
      const expanded = await expandProject(created.project.id, { format, targetLength, audience, freedom }, token)
      const directions = (expanded.revision.content as { directions?: unknown[] }).directions || []
      const parsedDirections = directions.map((d: unknown) => d as Direction)
      store.setDirections(parsedDirections)
      setShowDirections(true)
    } catch (e) {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: (e as Error).message || "Atlas could not expand this concept" } }))
    } finally {
      setLoading(false)
    }
  }

  const handleBuildStory = async () => {
    if (!store.selectedDirectionIndices.length || !store.cloudProjectId) {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Select at least one direction first" } }))
      return
    }
    const token = await getToken()
    if (!token) return
    ;(window as unknown as Record<string, unknown>).__atlas_token__ = token
    setLoading(true)
    try {
      const picks = store.selectedDirectionIndices.map((i) => store.directions[i]).filter(Boolean)
      const developed = await developProject(store.cloudProjectId, picks, { format, targetLength, audience, freedom }, token)
      const draft = (developed.revision.content as { title: string; logline: string; theme: string; visualLanguage: string; outline: string; scenes: unknown[]; episodes: unknown[] })
      const story = {
        title: String(draft.title),
        logline: String(draft.logline),
        theme: String(draft.theme),
        visualLanguage: String(draft.visualLanguage),
        outline: String(draft.outline),
        scenes: Array.isArray(draft.scenes) ? draft.scenes.map((s: unknown) => s as { title: string; summary: string; script: string; shots: { subject: string; camera: string; duration: number }[] }) : [],
        episodes: Array.isArray(draft.episodes) ? draft.episodes.map((e: unknown) => e as { title: string; status: string }) : [],
      }
      store.setStory(story)
      setStoryTitle(story.title)
      setStoryLogline(story.logline)
      setStoryTheme(story.theme)
      setStoryStyle(story.visualLanguage)
      setStoryOutline(story.outline)
      setShowDirections(false)
      setShowStory(true)
      store.updateTitle(story.title)
    } catch (e) {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: (e as Error).message || "Atlas could not build this story" } }))
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    const token = await getToken()
    if (!token || !store.cloudProjectId) return
    ;(window as unknown as Record<string, unknown>).__atlas_token__ = token
    try {
      const content = { title: storyTitle, logline: storyLogline, theme: storyTheme, visualLanguage: storyStyle, outline: storyOutline, scenes: store.story?.scenes, episodes: store.story?.episodes }
      const hash = await hashContent(content)
      await approveProject(store.cloudProjectId, "story", hash, token)
      store.setStoryApproved(true)
      store.setStoryHash(hash)
      store.setStage(Math.max(store.stage, 1))
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Story greenlit. Moving to planning." } }))
      navigate("/studio/plan")
    } catch (e) {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: (e as Error).message || "Approval failed" } }))
    }
  }

  if (showStory || store.story) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center gap-4 mb-6 border-b border-[#1e2130] pb-1">
          {(["brief", "script", "episodes"] as const).map((doc) => (
            <button
              key={doc}
              onClick={() => setActiveDoc(doc)}
              className={cn(
                "pb-3 text-sm font-medium capitalize transition-colors relative",
                activeDoc === doc ? "text-[#f59e0b]" : "text-[#6b7280] hover:text-[#9ca3af]"
              )}
            >
              {doc}
              {activeDoc === doc && <motion.div layoutId="doc-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#f59e0b]" />}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-4">
            {activeDoc === "brief" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <label className="block">
                  <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Working title</span>
                  <input className="input" value={storyTitle} onChange={(e) => setStoryTitle(e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Logline</span>
                  <textarea className="input" rows={3} value={storyLogline} onChange={(e) => setStoryLogline(e.target.value)} />
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Theme</span>
                    <textarea className="input" rows={4} value={storyTheme} onChange={(e) => setStoryTheme(e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Visual language</span>
                    <textarea className="input" rows={4} value={storyStyle} onChange={(e) => setStoryStyle(e.target.value)} />
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Story outline</span>
                  <textarea className="input" rows={12} value={storyOutline} onChange={(e) => setStoryOutline(e.target.value)} />
                </label>
              </motion.div>
            )}

            {activeDoc === "script" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                {store.story?.scenes.map((scene, i) => (
                  <div key={i} className="surface p-4">
                    <p className="text-xs font-medium text-[#f59e0b] mb-2">Scene {i + 1} · {scene.title}</p>
                    <textarea className="input" rows={7} defaultValue={scene.script} />
                  </div>
                ))}
              </motion.div>
            )}

            {activeDoc === "episodes" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                {store.story?.episodes.map((ep, i) => (
                  <div key={i} className="surface p-4 flex items-center gap-4">
                    <span className="text-xs font-mono text-[#6b7280] w-8">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex-1">
                      <input className="input" defaultValue={ep.title} />
                    </div>
                    <select className="input w-40" defaultValue={ep.status}>
                      <option>Concept</option>
                      <option>Current draft</option>
                      <option>Draft</option>
                      <option>Approved master ready</option>
                    </select>
                  </div>
                ))}
              </motion.div>
            )}
          </div>

          {/* Producer Panel */}
          <aside className="surface p-5 h-fit sticky top-20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-[#f59e0b]/10 flex items-center justify-center text-[#f59e0b] text-sm">✦</div>
              <div>
                <p className="text-sm font-medium text-[#f0f1f5]">Atlas Producer</p>
                <p className="text-xs text-[#6b7280]">Creative collaborator</p>
              </div>
            </div>
            <div className="space-y-3 mb-4">
              <p className="text-xs font-medium text-[#9ca3af]">What I&apos;m protecting</p>
              <ul className="space-y-1.5 text-xs text-[#9ca3af]">
                <li>• Your central premise stays intact.</li>
                <li>• The emotional turn lands before the spectacle.</li>
                <li>• Every scene must earn its render cost.</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg bg-[#f59e0b]/5 border border-[#f59e0b]/10 mb-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#f59e0b] mb-1">Visible assumption</p>
              <p className="text-xs text-[#9ca3af]">Atlas is developing this as a {format.toLowerCase()} for {targetLength}, using the selected visual language.</p>
            </div>
            <div className="flex gap-2">
              <input
                className="input flex-1 text-xs"
                placeholder="Ask for a rewrite or new angle…"
                value={producerNote}
                onChange={(e) => setProducerNote(e.target.value)}
              />
              <button className="p-2 rounded-lg bg-[#0f1117] border border-[#1e2130] hover:border-[#2d3148] text-[#f59e0b]">
                <MessageSquare className="w-4 h-4" />
              </button>
            </div>
          </aside>
        </div>

        {/* Greenlight */}
        <div className="gate-bar mt-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#10b981]/10 flex items-center justify-center">
              <Check className="w-4 h-4 text-[#10b981]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#f0f1f5]">Ready to lock the story?</p>
              <p className="text-xs text-[#9ca3af]">You can still revise it later. Atlas will show exactly what changes.</p>
            </div>
          </div>
          <button onClick={store.storyApproved ? () => navigate("/studio/plan") : handleApprove} className="btn-primary" disabled={loading}>
            {store.storyApproved ? "Open shot plan →" : loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Greenlight story →"}
          </button>
        </div>
      </motion.div>
    )
  }

  if (showDirections || (store.directions.length > 0 && !store.story)) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="eyebrow mb-1">Three ways in</p>
            <h2 className="heading-serif text-2xl">Choose a direction—or merge the best parts.</h2>
          </div>
          <button onClick={() => setShowDirections(false)} className="btn-subtle">Edit concept</button>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {store.directions.map((d, i) => (
            <button
              key={i}
              onClick={() => store.toggleDirection(i)}
              className={cn(
                "surface p-5 text-left transition-all hover:border-[#2d3148]",
                store.selectedDirectionIndices.includes(i) && "border-[#f59e0b]/40 bg-[#f59e0b]/[0.02]"
              )}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#6b7280]">Direction 0{i + 1} · {d.tone}</span>
                {store.selectedDirectionIndices.includes(i) && (
                  <span className="w-5 h-5 rounded-full bg-[#f59e0b] text-[#08090d] text-xs font-bold flex items-center justify-center">✓</span>
                )}
              </div>
              <h3 className="heading-sans text-base mb-2">{d.title}</h3>
              <p className="text-sm text-[#9ca3af] mb-3">{d.logline}</p>
              <ul className="space-y-1">
                {d.hooks?.slice(0, 3).map((h, hi) => (
                  <li key={hi} className="text-xs text-[#6b7280]">• {h}</li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between surface p-4">
          <span className="text-sm text-[#9ca3af]">
            {store.selectedDirectionIndices.length
              ? `${store.selectedDirectionIndices.length} direction${store.selectedDirectionIndices.length > 1 ? "s" : ""} selected`
              : "Select one or more directions"}
          </span>
          <button
            onClick={handleBuildStory}
            disabled={!store.selectedDirectionIndices.length || loading}
            className="btn-primary"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Build my story →"}
          </button>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-1 text-[#f59e0b]">Stage 1 · Develop</p>
          <h1 className="heading-display text-3xl">What are we making?</h1>
          <p className="text-sm text-[#9ca3af] mt-1">Start rough. Atlas will find the possibilities—and show its assumptions.</p>
        </div>
        <span className="text-xs text-[#10b981] font-medium">Text planning · no GPU spend</span>
      </div>

      <div className="max-w-3xl space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Your concept</span>
          <textarea
            className="input"
            rows={6}
            placeholder="A retired astronaut begins receiving radio messages from the daughter she left on Earth—but the messages are arriving 40 years too early."
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
          />
          <p className="text-xs text-[#6b7280] mt-1.5">Paste a sentence, a treatment, or everything you have.</p>
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Format</span>
            <select className="input" value={format} onChange={(e) => setFormat(e.target.value)}>
              <option>Short film</option>
              <option>Feature film</option>
              <option>Series</option>
              <option>Music video</option>
              <option>Social campaign</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Target length</span>
            <select className="input" value={targetLength} onChange={(e) => setTargetLength(e.target.value)}>
              <option>3–5 minutes</option>
              <option>8–12 minutes</option>
              <option>20–30 minutes</option>
              <option>Feature length</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Audience</span>
            <input className="input" placeholder="Curious sci-fi fans, 16+" value={audience} onChange={(e) => setAudience(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Creative freedom</span>
            <select className="input" value={freedom} onChange={(e) => setFreedom(e.target.value)}>
              <option>Surprise me</option>
              <option>Balanced collaboration</option>
              <option>Stay close to my concept</option>
            </select>
          </label>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg bg-[#10b981]/5 border border-[#10b981]/10">
          <Check className="w-4 h-4 text-[#10b981] shrink-0" />
          <div>
            <p className="text-xs font-medium text-[#f0f1f5]">Protected automatically</p>
            <p className="text-xs text-[#9ca3af]">Secure sign-in and server-side usage limits protect generation requests.</p>
          </div>
        </div>

        <button onClick={handleExplore} disabled={loading} className="btn-primary text-base px-6 py-3">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Explore this idea"} <Sparkles className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  )
}

async function hashContent(content: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(content))
  return Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)))
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("")
}
